import {
  Matter,
  MatterStatus,
  MatterType,
  Person,
  Sponsor,
  MatterHistory,
  MatterAttachment,
  FilterState,
  SortState,
  SearchResponse,
  FilterOptions,
  Committee,
} from '../types/legislation';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001/api';

// Search matters with filters
export const searchMatters = async (
  filters: FilterState,
  sort: SortState,
  page: number = 0,
  pageSize: number = 20
): Promise<SearchResponse> => {
  const params = new URLSearchParams();

  if (filters.search) {
    params.set('q', filters.search);
    if (filters.searchBody) {
      params.set('searchBody', 'true');
    }
    if (filters.searchAttachments) {
      params.set('searchAttachments', 'true');
    }
  }
  if (filters.statuses.length > 0) {
    params.set('status', filters.statuses.join(','));
  }
  if (filters.types.length > 0) {
    params.set('type', filters.types.join(','));
  }
  if (filters.sponsors.length > 0) {
    params.set('sponsor', filters.sponsors.join(','));
  }
  if (filters.bodyId) {
    params.set('bodyId', filters.bodyId.toString());
  }
  if (filters.dateFrom) {
    params.set('dateFrom', filters.dateFrom);
  }
  if (filters.dateTo) {
    params.set('dateTo', filters.dateTo);
  }
  if (filters.dateField) {
    params.set('dateField', filters.dateField);
  }
  if (filters.session) {
    params.set('session', filters.session);
  }

  params.set('orderBy', sort.field);
  params.set('orderDir', sort.direction);
  params.set('page', page.toString());
  params.set('pageSize', pageSize.toString());

  const response = await fetch(`${API_BASE_URL}/matters?${params}`);
  if (!response.ok) {
    throw new Error('Failed to search matters');
  }
  return response.json();
};

// Get filter options (statuses, types, persons)
export const fetchFilterOptions = async (): Promise<FilterOptions> => {
  const [statuses, types, persons] = await Promise.all([
    fetchMatterStatuses(),
    fetchMatterTypes(),
    fetchPersons(),
  ]);

  return { statuses, types, persons };
};

// Get matter statuses
export const fetchMatterStatuses = async (): Promise<MatterStatus[]> => {
  const response = await fetch(`${API_BASE_URL}/matterstatuses`);
  if (!response.ok) {
    throw new Error('Failed to fetch matter statuses');
  }
  return response.json();
};

// Get matter types
export const fetchMatterTypes = async (): Promise<MatterType[]> => {
  const response = await fetch(`${API_BASE_URL}/mattertypes`);
  if (!response.ok) {
    throw new Error('Failed to fetch matter types');
  }
  return response.json();
};

// Get persons (for sponsor autocomplete)
export const fetchPersons = async (): Promise<Person[]> => {
  const response = await fetch(`${API_BASE_URL}/persons`);
  if (!response.ok) {
    throw new Error('Failed to fetch persons');
  }
  return response.json();
};

// Get single matter details
export const fetchMatter = async (matterId: number): Promise<Matter> => {
  const response = await fetch(`${API_BASE_URL}/matters/${matterId}`);
  if (!response.ok) {
    throw new Error('Failed to fetch matter');
  }
  return response.json();
};

// Get matter sponsors
export const fetchMatterSponsors = async (matterId: number): Promise<Sponsor[]> => {
  const response = await fetch(`${API_BASE_URL}/matters/${matterId}/sponsors`);
  if (!response.ok) {
    throw new Error('Failed to fetch sponsors');
  }
  return response.json();
};

// Get matter history
export const fetchMatterHistory = async (matterId: number): Promise<MatterHistory[]> => {
  const response = await fetch(`${API_BASE_URL}/matters/${matterId}/histories`);
  if (!response.ok) {
    throw new Error('Failed to fetch history');
  }
  return response.json();
};

// Get matter attachments
export const fetchMatterAttachments = async (matterId: number): Promise<MatterAttachment[]> => {
  const response = await fetch(`${API_BASE_URL}/matters/${matterId}/attachments`);
  if (!response.ok) {
    throw new Error('Failed to fetch attachments');
  }
  return response.json();
};

// Build attachment file URL
export const getAttachmentFileUrl = (matterId: number, attachmentId: number): string => {
  return `${API_BASE_URL}/matters/${matterId}/attachments/${attachmentId}/file`;
};

// Get available sessions/years (derive from current year going back)
export const getAvailableSessions = (): string[] => {
  const currentYear = new Date().getFullYear();
  const sessions: string[] = [];
  for (let year = currentYear; year >= currentYear - 10; year--) {
    sessions.push(year.toString());
  }
  return sessions;
};

// Get tracked committees
export const fetchTrackedCommittees = async (): Promise<Committee[]> => {
  const response = await fetch(`${API_BASE_URL}/committees/tracked`);
  if (!response.ok) {
    throw new Error('Failed to fetch tracked committees');
  }
  return response.json();
};
